import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./admin-dashboard.css";

// ✅ Fix
// const BASE_URL = "https://suvidha-backend-353j.onrender.com";
const BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, "") || "https://api.suvidhaeldercare.com";

function authHeaders() {
  const t = localStorage.getItem("token");
  return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const TYPE_ICON = {
  new_booking:               "🛠",
  new_subscription:          "💳",
  booking_confirmed:         "🎉",
  booking_payment_received:  "✅",
  subscription_activated:    "🎉",
  new_enquiry:               "📬",
};

// ── Notification Dropdown ─────────────────────────────────
function NotifDropdown({ onClose }) {
  const [notifs,  setNotifs]  = useState([]);
  const [unread,  setUnread]  = useState(0);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const res  = await fetch(`${BASE_URL}/api/notifications`, { headers: authHeaders() });
      const data = await res.json();
      setNotifs(data.notifications || []);
      setUnread(data.unread || 0);
    } catch(e) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Close when clicking outside
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markAllRead = async () => {
    await fetch(`${BASE_URL}/api/notifications/read-all`, { method:"POST", headers: authHeaders() });
    setNotifs(n => n.map(x => ({ ...x, is_read: 1 })));
    setUnread(0);
  };

  const markOne = async (id) => {
    await fetch(`${BASE_URL}/api/notifications/${id}/read`, { method:"POST", headers: authHeaders() });
    setNotifs(n => n.map(x => x.id === id ? { ...x, is_read: 1 } : x));
    setUnread(u => Math.max(0, u - 1));
  };

  const fmtTime = (d) => {
    const diff = Math.floor((Date.now() - new Date(d)) / 1000);
    if (diff < 60)   return "just now";
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    return new Date(d).toLocaleDateString("en-IN", { day:"numeric", month:"short" });
  };

  return (
    <div ref={ref} style={{
      position: "fixed",
      top: 68, right: 8,
      width: "min(380px, calc(100vw - 16px))",
      maxHeight: "min(480px, calc(100vh - 80px))",
      background: "#fff", borderRadius: 18,
      boxShadow: "0 20px 60px rgba(0,0,0,.18)",
      border: "1px solid #E2E8F0",
      zIndex: 9999, overflow: "hidden",
      display: "flex", flexDirection: "column"
    }}>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 18px 12px",
        borderBottom: "1px solid #F1F5F9"
      }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: "#1E293B" }}>
          🔔 Notifications
          {unread > 0 && (
            <span style={{
              marginLeft: 8, background: "#DC2626", color: "#fff",
              fontSize: 11, fontWeight: 800,
              padding: "2px 7px", borderRadius: 999
            }}>{unread}</span>
          )}
        </div>
        {unread > 0 && (
          <button onClick={markAllRead} style={{
            fontSize: 12, color: "#1F4E8C", fontWeight: 700,
            background: "none", border: "none", cursor: "pointer",
            fontFamily: "inherit"
          }}>Mark all read</button>
        )}
      </div>

      {/* List */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
            Loading...
          </div>
        ) : notifs.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🔕</div>
            <div style={{ color: "#94A3B8", fontSize: 14 }}>No notifications yet</div>
          </div>
        ) : (
          notifs.map(n => (
            <div
              key={n.id}
              onClick={() => { if (!n.is_read) markOne(n.id); }}
              style={{
                display: "flex", gap: 12, alignItems: "flex-start",
                padding: "14px 18px",
                background: n.is_read ? "#fff" : "#EFF6FF",
                borderBottom: "1px solid #F8FAFC",
                cursor: n.is_read ? "default" : "pointer",
                transition: "background .15s"
              }}
            >
              {/* Icon */}
              <div style={{
                width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                background: n.is_read ? "#F1F5F9" : "#DBEAFE",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18
              }}>
                {TYPE_ICON[n.type] || "📢"}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: n.is_read ? 600 : 800,
                  fontSize: 13, color: "#1E293B",
                  marginBottom: 2
                }}>{n.title}</div>
                <div style={{
                  fontSize: 12, color: "#64748B",
                  lineHeight: 1.4,
                  whiteSpace: "normal", wordBreak: "break-word"
                }}>{n.message}</div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                  {fmtTime(n.created_at)}
                </div>
              </div>

              {/* Unread dot */}
              {!n.is_read && (
                <div style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: "#1F4E8C", flexShrink: 0, marginTop: 4
                }} />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Main Topbar ───────────────────────────────────────────
export default function Topbar({ title, subtitle }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen,   setNotifOpen]   = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const profileRef = useRef(null);
  const notifRef   = useRef(null);
  const navigate   = useNavigate();

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  })();
  const adminName    = user?.full_name || "Admin";
  const role         = user?.role      || "admin";
  const avatarLetter = adminName.trim()[0]?.toUpperCase() || "A";

  // Poll unread count every 30s
  const fetchUnread = useCallback(async () => {
    try {
      const res  = await fetch(`${BASE_URL}/api/notifications`, { headers: authHeaders() });
      const data = await res.json();
      setUnreadCount(data.unread || 0);
    } catch(e) {}
  }, []);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    if (!window.confirm("Are you sure you want to logout?")) return;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <header className="adm-topbar">

      {/* LEFT — Title */}
      <div className="adm-top-left">
        <div className="adm-title">{title}</div>
        <div className="adm-subtitle">{subtitle}</div>
      </div>

      {/* RIGHT */}
      <div className="adm-top-right">

        {/* Home */}
        <Link to="/" title="Go to Home" style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "7px 14px", borderRadius: 10,
          border: "1px solid #CBD5E1", background: "#F8FAFC",
          color: "#374151", fontWeight: 600, fontSize: 13,
          textDecoration: "none"
        }}>
          🏠 <span>Home</span>
        </Link>

        {/* Notification Bell */}
        <div ref={notifRef} style={{ position: "relative" }}>
          <button
            onClick={() => { setNotifOpen(o => !o); setProfileOpen(false); }}
            className="adm-icon-btn"
            title="Notifications"
            style={{ position: "relative" }}
          >
            🔔
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: -4, right: -4,
                background: "#DC2626", color: "#fff",
                fontSize: 10, fontWeight: 900,
                width: 18, height: 18, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #fff"
              }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <NotifDropdown onClose={() => setNotifOpen(false)} />
          )}
        </div>

        {/* Profile */}
        <div className="adm-profile" ref={profileRef}>
          <button className="adm-profile-btn" onClick={() => { setProfileOpen(o => !o); setNotifOpen(false); }}>
            <div className="adm-avatar">{avatarLetter}</div>
            <div className="adm-profile-meta">
              <strong>{adminName}</strong>
              <span style={{ textTransform: "capitalize" }}>{role.replace("_", " ")}</span>
            </div>
            <span style={{ opacity: 0.6 }}>▾</span>
          </button>

          {profileOpen && (
            <div className="adm-dropdown">
              <div style={{
                padding: "10px 16px", borderBottom: "1px solid #eee",
                fontSize: 13, color: "#64748B"
              }}>
                Signed in as<br />
                <strong style={{ color: "#1E293B" }}>{adminName}</strong><br />
                <span style={{ textTransform: "capitalize", fontSize: 11, color: "#94A3B8" }}>
                  {role.replace("_", " ")}
                </span>
              </div>
              <Link to="/" onClick={() => setProfileOpen(false)} style={{
                display: "block", padding: "10px 16px",
                fontSize: 14, color: "#374151",
                textDecoration: "none", fontWeight: 600
              }}>🏠 Go to Home</Link>
              <button onClick={() => setProfileOpen(false)}>⚙️ Settings</button>
              <button className="adm-danger" onClick={handleLogout}
                style={{ color: "#DC2626", fontWeight: 700 }}>
                🚪 Logout
              </button>
            </div>
          )}
        </div>

      </div>
    </header>
  );
}